// Reusable wizard-step header. One source of truth for the title +
// subtitle treatment so every step looks identical:
//   - left-aligned (was previously center-aligned per-step)
//   - optional icon tile to the left (mirrors the sidebar stepper icons
//     so users get a strong visual anchor of which step they're on)
//   - clear bottom-border separator between the header block and the
//     step's content below
//
// Used by all 7 step components in pages/app/components/Step*.tsx. Each
// step passes its own Remix Icon class (same one as in AppLayout.tsx's
// `steps` array) so the icons stay in sync between the sidebar nav and
// the page header.

import type { ReactNode } from 'react';

interface StepHeaderProps {
  /** Remix Icon class string (e.g. 'ri-smartphone-line'). Matches the
   *  per-step icon defined in AppLayout.tsx. Optional — fallback paths
   *  (e.g. error states) may render without one. */
  icon?: string;
  title: string;
  /** Optional. Accepts string or JSX so steps can include conditional
   *  fragments (e.g. carousel-specific notes) inline. */
  subtitle?: ReactNode;
}

export default function StepHeader({ icon, title, subtitle }: StepHeaderProps) {
  // min-h locks the header to a consistent height across all 7 steps,
  // even when subtitle line-count varies (some fit on 1 line, some wrap
  // to 2 depending on length + viewport width). Without this lock the
  // content area below shifts ~24px between steps when the subtitle
  // wrap differs.
  return (
    <div className="flex-shrink-0 min-h-[60px] mb-4 pb-3 -mx-6 px-6 border-b-2 border-white flex items-center gap-3">
      {icon && (
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <i className={`${icon} text-xl`} />
        </div>
      )}
      <div className="min-w-0">
        <h2 className="font-display text-base md:text-lg font-black text-dark mb-0.5">
          {title}
        </h2>
        {subtitle && (
          <p className="text-dark-muted text-sm leading-snug">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
